import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Button, Form, Modal } from "react-bootstrap";
import { getStaffOrder, setOrderStatus, addDispatch } from "../../services/requirementsService";
import { errorToast, successToast } from "../Toasts/Toasts";

// CMS: one order. Move it along, record a dispatch (which items, how many,
// vehicle, challan) - the delivered quantities and the status follow.
const inr = (n) => (n === null || n === undefined ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);
const when = (d) => (d ? new Date(d).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const NEXT = { "Awaiting PO": ["Confirmed", "Cancelled"], Confirmed: ["Preparing", "Ready for Dispatch", "Cancelled"], Preparing: ["Ready for Dispatch", "Cancelled"], "Ready for Dispatch": ["Cancelled"], Delivered: ["Completed"] };

const OrderDesk = () => {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ vehicle: "", driverName: "", driverPhone: "", challanNumber: "", notes: "", challan: null, qty: {} });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setO(await getStaffOrder(id)); } catch (e) { errorToast("Could not load"); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openDispatch = () => {
    const qty = {};
    for (const it of o.items) qty[it.id] = Math.max(0, Number(it.quantity) - Number(it.deliveredQty));
    setForm({ vehicle: "", driverName: "", driverPhone: "", challanNumber: "", notes: "", challan: null, qty });
    setModal(true);
  };
  const dispatch = async (e) => {
    e.preventDefault();
    const items = Object.entries(form.qty).filter(([, q]) => Number(q) > 0).map(([orderItemId, quantity]) => ({ orderItemId: Number(orderItemId), quantity: Number(quantity) }));
    if (!items.length) return errorToast("Enter a quantity for at least one item");
    const fd = new FormData();
    fd.append("items", JSON.stringify(items));
    for (const k of ["vehicle", "driverName", "driverPhone", "challanNumber", "notes"]) if (form[k]) fd.append(k, form[k]);
    if (form.challan) fd.append("challan", form.challan);
    setBusy(true);
    try {
      const r = await addDispatch(id, fd);
      if (r?.success) { successToast("Dispatch recorded"); setModal(false); load(); } else errorToast(r?.message);
    } catch (err) { errorToast(err?.message); } finally { setBusy(false); }
  };
  const move = async (status) => {
    try { const r = await setOrderStatus(id, status); if (r?.success) load(); else errorToast(r?.message); } catch (e) { errorToast(e?.message); }
  };

  if (!o) return null;
  const canDispatch = ["Confirmed", "Preparing", "Ready for Dispatch", "Dispatched", "Partially Delivered"].includes(o.status);
  const addr = o.deliveryAddress;

  return (
    <div className="page-body px-0 py-lg-2 py-1 mt-0 mt-lg-3 ms-3">
      <div className="container-fluid p-0">
        <div className="row-title px-2 mb-2 d-flex justify-content-between align-items-start flex-wrap gap-2">
          <div>
            <div className="small"><Link to="/orders">Orders</Link> / {o.orderNumber}</div>
            <h6 className="fw-bold mb-0">{o.orderNumber} · {o.organizationName} <span className="badge bg-light text-dark">{o.status}</span></h6>
            <div className="small text-muted">From <Link to={`/requirements/${o.requirementNumber}`}>{o.requirementNumber}</Link>{o.deliveryDate ? ` · needed by ${o.deliveryDate}` : ""}{o.customerPoNumber ? ` · PO ${o.customerPoNumber}` : ""}</div>
            {addr && <div className="small text-muted">Deliver to: {[addr.line1, addr.city, addr.pincode].filter(Boolean).join(", ")}</div>}
          </div>
          <div className="d-flex gap-2">
            {(NEXT[o.status] || []).map((s) => <Button key={s} size="sm" variant={s === "Cancelled" ? "outline-danger" : "outline-secondary"} onClick={() => move(s)}>{s}</Button>)}
            {canDispatch && <Button size="sm" variant="success" onClick={openDispatch}>Record dispatch</Button>}
          </div>
        </div>

        <div className="row g-3">
          <div className="col-lg-7">
            <div className="card"><div className="card-body p-0">
              <table className="table table-sm mb-0">
                <thead><tr><th>#</th><th>Item</th><th className="text-end">Ordered</th><th className="text-end">Delivered</th><th className="text-end">Rate</th><th className="text-end">Amount</th></tr></thead>
                <tbody>{o.items.map((it) => <tr key={it.id}><td>{it.lineNo}</td><td>{it.item}{it.size ? <div className="small text-muted">{it.size}</div> : null}</td><td className="text-end">{Number(it.quantity)} {it.unit || ""}</td><td className="text-end">{Number(it.deliveredQty)}</td><td className="text-end">{inr(it.rate)}</td><td className="text-end">{inr(it.amount)}</td></tr>)}</tbody>
                <tfoot><tr className="fw-bold"><td colSpan={5} className="text-end">Total incl. GST</td><td className="text-end">{inr(o.totals?.grand)}</td></tr></tfoot>
              </table>
            </div></div>
          </div>
          <div className="col-lg-5">
            <div className="card"><div className="card-body">
              <h6 className="fw-bold">Dispatches</h6>
              {o.dispatches.length ? o.dispatches.map((d) => (
                <div key={d.id} className="border-bottom py-2">
                  <div className="fw-semibold">#{d.dispatchNo} · {when(d.dispatchedAt)}</div>
                  <div className="small text-muted">{[d.vehicle, d.driverName, d.driverPhone, d.challanNumber && `Challan ${d.challanNumber}`].filter(Boolean).join(" · ")}</div>
                  <div className="small">{d.items.map((it) => { const oi = o.items.find((x) => x.id === it.orderItemId); return `${oi ? oi.item : "?"} × ${it.quantity}`; }).join(", ")}</div>
                </div>
              )) : <div className="small text-muted">Nothing dispatched yet.</div>}
              {o.documents?.length > 0 && <div className="mt-2">{o.documents.map((doc) => <div key={doc.id} className="small"><a href={doc.url} target="_blank" rel="noreferrer">{doc.fileName}</a> ({doc.kind})</div>)}</div>}
              {o.customerConfirmedAt && <div className="small mt-2 text-success">Customer confirmed receipt {when(o.customerConfirmedAt)}{o.customerRemarks ? ` — "${o.customerRemarks}"` : ""}</div>}
            </div></div>
          </div>
        </div>
      </div>

      <Modal show={modal} onHide={() => setModal(false)} size="lg">
        <Form onSubmit={dispatch}>
          <Modal.Header closeButton><Modal.Title>Record a dispatch</Modal.Title></Modal.Header>
          <Modal.Body>
            <table className="table table-sm">
              <thead><tr><th>Item</th><th className="text-end">Remaining</th><th style={{ width: 140 }}>Sending now</th></tr></thead>
              <tbody>{o.items.map((it) => { const rem = Number(it.quantity) - Number(it.deliveredQty); return (
                <tr key={it.id}><td>{it.item}</td><td className="text-end">{rem}</td><td><Form.Control size="sm" type="number" min={0} max={rem} step="1" value={form.qty[it.id] ?? 0} onChange={(e) => setForm({ ...form, qty: { ...form.qty, [it.id]: e.target.value } })} /></td></tr>
              ); })}</tbody>
            </table>
            <div className="row g-2">
              <div className="col-md-4"><Form.Label className="small mb-0">Vehicle</Form.Label><Form.Control size="sm" value={form.vehicle} onChange={(e) => setForm({ ...form, vehicle: e.target.value })} /></div>
              <div className="col-md-4"><Form.Label className="small mb-0">Driver</Form.Label><Form.Control size="sm" value={form.driverName} onChange={(e) => setForm({ ...form, driverName: e.target.value })} /></div>
              <div className="col-md-4"><Form.Label className="small mb-0">Driver mobile</Form.Label><Form.Control size="sm" value={form.driverPhone} onChange={(e) => setForm({ ...form, driverPhone: e.target.value })} /></div>
              <div className="col-md-4"><Form.Label className="small mb-0">Challan number</Form.Label><Form.Control size="sm" value={form.challanNumber} onChange={(e) => setForm({ ...form, challanNumber: e.target.value })} /></div>
              <div className="col-md-8"><Form.Label className="small mb-0">Challan (PDF or photo)</Form.Label><Form.Control size="sm" type="file" accept="image/*,.pdf" onChange={(e) => setForm({ ...form, challan: e.target.files[0] })} /></div>
              <div className="col-12"><Form.Label className="small mb-0">Notes</Form.Label><Form.Control size="sm" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
          </Modal.Body>
          <Modal.Footer><Button type="submit" variant="success" disabled={busy}>{busy ? "Saving…" : "Record dispatch"}</Button></Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default OrderDesk;
